import PlatformFeatureEnum from '@constants/enums/platform/feature';

import i18n from '@plugins/i18n';

export default [
    {
        path: '/settings',
        name: 'Settings',
        component: () => import('@/views/Settings.vue'),
        meta: {
            title: i18n.t('settings'),
            heading: i18n.t('settings')
        },
        children: [
            {
                path: '/settings/introduction',
                name: 'SettingsIntroduction',
                component: () => import('@/views/SettingsIntroduction.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.introduction'),
                    heading: i18n.t('enums.settings_tabs.introduction'),
                    active: (org, auth) => {
                        if (auth.is_investor && org.is_investor && !org.adviser_account.disable_investor_welcome_page) {
                            return true;
                        }

                        if (auth.is_adviser && org.is_investor && !org.adviser_account.disable_investor_welcome_page) {
                            return true;
                        }

                        return false;
                    }
                }
            },
            {
                path: '/settings/profile',
                name: 'SettingsProfile',
                component: () => import('@/views/SettingsProfile.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.profile'),
                    heading: i18n.t('enums.settings_tabs.profile'),
                    active: org => org.is_investor && org.is_individual
                }
            },
            {
                path: '/settings/firm-information',
                name: 'SettingsFirmInformation',
                component: () => import('@/views/SettingsFirmInformation.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.firm_information'),
                    heading: i18n.t('enums.settings_tabs.firm_information'),
                    active: org => org.is_adviser || org.is_provider || org.is_fund_manager || org.is_company
                }
            },
            {
                path: '/settings/corporate-profile',
                name: 'SettingsCorporateProfile',
                component: () => import('@/views/SettingsCorporateProfile.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.corporate_profile'),
                    heading: i18n.t('enums.settings_tabs.corporate_profile'),
                    active: org =>
                        org.is_investor &&
                        (org.is_trust || org.is_corporate || org.is_pension || org.is_joint)
                }
            },
            {
                path: '/settings/appropriateness',
                name: 'SettingsAppropriateness',
                component: () => import('@/views/SettingsAppropriateness.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.appropriateness'),
                    heading: i18n.t('enums.settings_tabs.appropriateness'),
                    active: org => org.is_investor && org.is_individual
                }
            },
            {
                path: '/settings/billing',
                name: 'SettingsBilling',
                component: () => import('@/views/SettingsBilling.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.billing'),
                    heading: i18n.t('enums.settings_tabs.billing'),
                    active: (org, auth) => org.is_investor && (auth.is_investor || auth.is_adviser)
                }
            },
            {
                path: '/settings/investor-bank-account',
                name: 'SettingsInvestorBankAccount',
                component: () => import('@/views/SettingsInvestorBankAccount.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.investor_bank_account'),
                    heading: i18n.t('enums.settings_tabs.investor_bank_account'),
                    active: (org, auth) => org.is_investor && (auth.is_investor || auth.is_adviser)
                }
            },
            {
                path: '/settings/communication',
                name: 'SettingsCommunication',
                component: () => import('@/views/SettingsCommunication.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.communication'),
                    heading: i18n.t('enums.settings_tabs.communication'),
                    active: (org, auth) => org.is_investor && (auth.is_investor || auth.is_adviser)
                }
            },
            {
                path: '/settings/intermediary',
                name: 'SettingsIntermediary',
                component: () => import('@/views/SettingsIntermediary.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.intermediary'),
                    heading: i18n.t('enums.settings_tabs.intermediary'),
                    active: (org, auth) => org.is_investor && (auth.is_investor || auth.is_adviser)
                }
            },
            {
                path: '/settings/client-integrations',
                name: 'SettingsClientIntegrations',
                component: () => import('@/views/SettingsClientIntegrations.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.client_integrations'),
                    heading: i18n.t('enums.settings_tabs.client_integrations'),
                    active: (org, auth) => !!(org.is_investor && (auth.is_adviser || auth.is_gi))
                }
            },
            {
                path: '/settings/financial',
                name: 'SettingsFinancial',
                component: () => import('@/views/SettingsFinancial.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.financial'),
                    heading: i18n.t('enums.settings_tabs.financial'),
                    active: (org, auth) =>
                        org.is_adviser && (auth.is_gi || auth.is_owner || auth.is_superuser || auth.is_accounts)
                }
            },
            {
                path: '/settings/users',
                name: 'SettingsUsers',
                component: () => import('@/views/SettingsUsers.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.users'),
                    heading: i18n.t('enums.settings_tabs.users'),
                    active: (org, auth) => {
                        // The users tab is available for these organisation types.
                        const isOrgWithUsers =
                            org.is_adviser || org.is_provider || org.is_fund_manager || org.is_company;

                        if (isOrgWithUsers) {
                            return true;
                        }

                        // For investor accounts, only show the tab if the
                        // authenticated user has permissions to manage other users.
                        const canAuthUserManageUsers =
                            auth.is_gi || auth.is_adviser || auth.is_provider || auth.is_fund_manager || auth.is_owner;

                        return org.is_investor && canAuthUserManageUsers;
                    }
                }
            },
            {
                path: '/settings/external-users',
                name: 'SettingsExternalUsers',
                component: () => import('@/views/SettingsUsers.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.external_users'),
                    heading: i18n.t('enums.settings_tabs.external_users'),
                    active: org => org.is_adviser
                }
            },
            {
                path: '/settings/agreements',
                name: 'SettingsAgreements',
                component: () => import('@/views/SettingsAgreements.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.agreements'),
                    heading: i18n.t('enums.settings_tabs.agreements'),
                    active: (org, auth) =>
                        !org.is_investor || (org.is_investor && (auth.is_investor || auth.is_adviser))
                }
            },
            {
                path: '/settings/providers',
                name: 'SettingsProviders',
                component: () => import('@/views/SettingsProviders.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.providers'),
                    heading: i18n.t('enums.settings_tabs.providers'),
                    active: (org, auth) => org.is_investor && org.is_individual && auth.is_gi
                }
            },
            {
                path: '/settings/additional-needs',
                name: 'SettingsAdditionalNeeds',
                component: () => import('@/views/SettingsAdditionalNeeds.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.additional_needs'),
                    heading: i18n.t('enums.settings_tabs.additional_needs'),
                    active: (org, auth, route) =>
                        org.is_investor &&
                        org.is_individual &&
                        (org.has_additional_needs || route.name === 'SettingsAdditionalNeeds')
                }
            },
            {
                path: '/settings/network-setup',
                name: 'SettingsNetworkSetup',
                component: () => import('@/views/SettingsNetworkSetup.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.network_setup'),
                    heading: i18n.t('enums.settings_tabs.network_setup'),
                    active: org => org.is_adviser
                }
            },
            {
                path: '/settings/branding',
                name: 'SettingsBranding',
                component: () => import('@/views/SettingsBranding.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.branding'),
                    heading: i18n.t('enums.settings_tabs.branding'),
                    active: org => org.is_adviser || org.is_provider || org.is_fund_manager
                }
            },
            {
                path: '/settings/visibility-groups',
                name: 'SettingsVisibilityGroups',
                component: () => import('@/views/SettingsVisibilityGroups.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.visibility_groups'),
                    heading: i18n.t('enums.settings_tabs.visibility_groups'),
                    active: (org, auth) => {
                        const features = Array.isArray(org.features) ? org.features : [];

                        return (
                            org.is_root ||
                            ((auth.is_gi || auth.is_superuser) &&
                                (features.includes(PlatformFeatureEnum.BASIC_VISIBILITY_GROUPS) ||
                                    features.includes(PlatformFeatureEnum.ADVANCED_VISIBILITY_GROUPS)))
                        );
                    }
                }
            },
            {
                path: '/settings/restricted-panels',
                name: 'SettingsRestrictedPanels',
                component: () => import('@/views/SettingsRestrictedPanels.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.restricted_panels'),
                    heading: i18n.t('enums.settings_tabs.restricted_panels'),
                    active: (org, auth) => org.is_adviser && auth.is_gi
                }
            },
            {
                path: '/settings/integrations',
                name: 'SettingsIntegrations',
                component: () => import('@/views/SettingsIntegrations.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.integrations'),
                    heading: i18n.t('enums.settings_tabs.integrations'),
                    active: org => org.is_adviser
                }
            },
            {
                path: '/settings/investors',
                name: 'SettingsInvestors',
                component: () => import('@/views/SettingsInvestors.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.investors'),
                    heading: i18n.t('enums.settings_tabs.investors'),
                    active: (org, auth) => auth.is_gi
                }
            },
            {
                path: '/settings/letters-of-authority',
                name: 'SettingsLettersOfAuthority',
                component: () => import('@/views/SettingsLettersOfAuthority.vue'),
                meta: {
                    title: i18n.t('settings') + ' - ' + i18n.t('enums.settings_tabs.letters_of_authority'),
                    heading: i18n.t('enums.settings_tabs.letters_of_authority'),
                    // active: (org, auth) => org.is_investor && (auth.is_fund_manager || auth.is_provider)
                    active: false
                }
            }
        ]
    }
];
